import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle, ShieldCheck, Trash2, Plus, Wallet } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function BeneficiaryManager() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ display_name: "", account_holder: "", account_number: "", ifsc: "", bank_name: "", upi_id: "", mobile: "" });
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/beneficiaries/mine").then(r => setItems(r.data));
  useEffect(() => { if (user) load(); }, [user]);

  if (!user) return null;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.account_holder || !form.account_number || !form.ifsc || !form.bank_name) {
      toast.error("Please fill account holder, account number, IFSC & bank name");
      return;
    }
    setSaving(true);
    try {
      await api.post("/beneficiaries", { ...form, mobile: form.mobile || user.mobile });
      toast.success("Beneficiary added & verified");
      setForm({ display_name: "", account_holder: "", account_number: "", ifsc: "", bank_name: "", upi_id: "", mobile: "" });
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not register");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Remove this beneficiary?")) return;
    await api.delete(`/beneficiaries/${id}`);
    load();
  };

  return (
    <div className="bg-white border border-stone-200 rounded-3xl p-6 md:p-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Wallet size={20} className="text-[#2D5A27]" />
          <div>
            <div className="heading font-bold text-stone-900">Genuine account (for receiving payments)</div>
            <div className="text-xs text-stone-500">A verified bank/UPI account is required to receive money from bookings.</div>
          </div>
        </div>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} size="sm" className="rounded-full bg-[#E05E36] hover:bg-[#c94e2a] gap-1" data-testid="add-beneficiary-btn"><Plus size={14} /> Add</Button>
        )}
      </div>

      {/* Existing beneficiaries */}
      <div className="space-y-2">
        {items.map(b => (
          <motion.div key={b.beneficiary_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-3 bg-stone-50 rounded-2xl p-3" data-testid={`beneficiary-${b.beneficiary_id}`}>
            <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${b.verified ? "bg-[#2D5A27] text-white" : "bg-stone-200 text-stone-500"}`}>
              {b.verified ? <CheckCircle size={18} /> : <Wallet size={18} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-stone-900 text-sm truncate">{b.display_name || b.account_holder}</div>
              <div className="text-[11px] text-stone-600 truncate">{b.bank_name} · {b.account_number_masked} · {b.ifsc}</div>
              {b.upi_id && <div className="text-[11px] text-stone-500">UPI: {b.upi_id}</div>}
            </div>
            <div className="flex items-center gap-1">
              {b.verified && <span className="text-[9px] font-extrabold uppercase text-[#2D5A27] bg-[#2D5A27]/10 rounded-full px-2 py-1">Verified</span>}
              {b.is_default && <span className="text-[9px] font-extrabold uppercase text-[#E05E36] bg-[#E05E36]/10 rounded-full px-2 py-1">Default</span>}
              <button onClick={() => remove(b.beneficiary_id)} className="text-stone-400 hover:text-red-600 p-1" data-testid={`del-beneficiary-${b.beneficiary_id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          </motion.div>
        ))}
        {items.length === 0 && !showForm && (
          <div className="text-center py-6 text-stone-500 text-sm">
            No beneficiary yet. Add one to activate payment receipts.
          </div>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-4 pt-4 border-t border-stone-100 grid md:grid-cols-2 gap-3">
          <Field label="Nickname (optional)"><Input value={form.display_name} onChange={e => set("display_name", e.target.value)} placeholder="Shop account, Home account…" className="rounded-xl" data-testid="ben-nickname" /></Field>
          <Field label="Account holder name" required><Input value={form.account_holder} onChange={e => set("account_holder", e.target.value)} className="rounded-xl" data-testid="ben-holder" /></Field>
          <Field label="Account number" required><Input value={form.account_number} onChange={e => set("account_number", e.target.value)} className="rounded-xl" data-testid="ben-account" /></Field>
          <Field label="IFSC code" required><Input value={form.ifsc} onChange={e => set("ifsc", e.target.value.toUpperCase())} placeholder="HDFC0001234" className="rounded-xl uppercase" data-testid="ben-ifsc" /></Field>
          <Field label="Bank name" required><Input value={form.bank_name} onChange={e => set("bank_name", e.target.value)} placeholder="HDFC Bank" className="rounded-xl" data-testid="ben-bank" /></Field>
          <Field label="UPI ID (optional)"><Input value={form.upi_id} onChange={e => set("upi_id", e.target.value)} placeholder="name@upi" className="rounded-xl" data-testid="ben-upi" /></Field>
          <div className="md:col-span-2 flex gap-2 pt-2">
            <Button onClick={save} disabled={saving} className="flex-1 rounded-full bg-[#2D5A27] hover:bg-[#24491f] gap-2" data-testid="save-beneficiary">
              <ShieldCheck size={16} /> {saving ? "Verifying…" : "Register & verify"}
            </Button>
            <Button onClick={() => setShowForm(false)} variant="ghost" className="rounded-full" data-testid="cancel-beneficiary">Cancel</Button>
          </div>
          <div className="md:col-span-2 text-[11px] text-stone-500 bg-amber-50 border border-amber-200 rounded-xl p-2">
            <b>Demo mode:</b> real deployments use a penny-drop / bank verification API. Here, valid-looking details are auto-marked verified.
          </div>
        </motion.div>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-widest text-stone-500">{label} {required && <span className="text-[#E05E36]">*</span>}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
